import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import Slider from 'components/slider/slider';
import GameCarousel from 'components/gameCarousel/gameCarousel';
import DailySpinWidget from 'components/dailySpinWidget/DailySpinWidget';
import BetsTable from 'components/betTable/betsTable';
import TableGamesCarousel from 'components/tableGamesCarousel/tableGamesCarousel';
import SearchBar from 'components/searchBar/fullSearchBar/searchBar';
import ProviderTicker from 'components/providerTicker/providerTicker';
import { useAppSelector } from 'store/hooks';
import { useGetCollectionsQuery, useGetRecentlyPlayedQuery } from 'store/gamesApi';
import {
  OriginalsIcon,
  RecentlyIcon,
  TableGamesIcon,
  LiveCasinoIcon,
  SlotsIcon,
  NewGamesIcon,
  GameShowsIcon,
} from 'components/icons';
import styles from '../App.module.scss';

interface APICollectionGame {
  gameId: number | string;
  title: string;
  imageUrl: string;
  isLive: boolean;
  order: number;
}

interface APICollection {
  id: number;
  slug: string;
  title: string;
  icon: string | null;
  order: number;
  displayLimit: number;
  games: APICollectionGame[];
}

const getCategoryIcon = (slug: string, title: string) => {
  const slugLower = slug.toLowerCase();
  const titleLower = title.toLowerCase();

  const props = { width: '20', height: '20', style: { color: 'var(--text-active)' } };

  if (slugLower.includes('live') || titleLower.includes('live'))
    return <LiveCasinoIcon {...props} />;
  if (slugLower.includes('slot') || titleLower.includes('slot')) return <SlotsIcon {...props} />;
  if (slugLower.includes('table') || titleLower.includes('table'))
    return <TableGamesIcon {...props} />;
  if (titleLower.includes('new')) return <NewGamesIcon {...props} />;
  if (slugLower.includes('show') || titleLower.includes('show'))
    return <GameShowsIcon {...props} />;

  return <OriginalsIcon {...props} />;
};

const mapCollectionGames = (apiGames: APICollectionGame[]): any[] => {
  return (apiGames || []).map((g) => ({
    id: g.gameId,
    name: g.title,
    imgUrl: g.imageUrl,
    playingCount: Math.floor(Math.random() * 300) + 50, // mock count
    isFavorite: false,
  }));
};

function Home() {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language?.slice(0, 2) || 'en';
  const { data: collectionsRes, isLoading: isCollectionsLoading } =
    useGetCollectionsQuery(currentLang);
  const isAuthenticated = useAppSelector((state) => state.auth.isAuthenticated);

  const { data: recentlyPlayedRes, isLoading: isRecentlyLoading } = useGetRecentlyPlayedQuery(
    undefined,
    {
      skip: !isAuthenticated,
    },
  );

  const collections: APICollection[] = useMemo(
    () => collectionsRes?.data?.collections || [],
    [collectionsRes],
  );
  const recentlyPlayedGames: any[] = useMemo(
    () => recentlyPlayedRes?.data || [],
    [recentlyPlayedRes],
  );

  const firstCollection = collections[0];
  const remainingCollections = collections.slice(1);

  return (
    <>
      <main className={styles.main}>
        <div className={styles.content}>
          {/* ── Promo Banner Row (40% empty / 60% slider) ── */}
          <div className={styles.promoRow}>
            <div className={styles.promoRight}>
              <Slider />
              <div className={styles.searchSection}>
                <SearchBar games={[]} />
              </div>
            </div>
            <div className={styles.promoLeft}>
              <DailySpinWidget />
            </div>
          </div>

          {isCollectionsLoading && collections.length === 0 ? (
            <>
              <div className={styles.carouselSection}>
                <GameCarousel loading={true} />
              </div>
              <div className={styles.carouselSection}>
                <GameCarousel loading={true} />
              </div>
            </>
          ) : (
            <>
              {isAuthenticated && isRecentlyLoading && recentlyPlayedGames.length === 0 && (
                <div className={styles.carouselSection}>
                  <GameCarousel loading={true} />
                </div>
              )}

              {isAuthenticated && recentlyPlayedGames?.length > 0 && (
                <div className={styles.carouselSection}>
                  <GameCarousel
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        <RecentlyIcon
                          width="20"
                          height="20"
                          style={{ color: 'var(--text-active)' }}
                        />
                        <span>{t('common.recentlyPlayed', 'Recently Played')}</span>
                      </div>
                    }
                    games={mapCollectionGames(recentlyPlayedGames)}
                    hideSeeAll={true}
                  />
                </div>
              )}

              {firstCollection && (
                <div className={styles.carouselSection}>
                  <GameCarousel
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {getCategoryIcon(firstCollection.slug, firstCollection.title)}
                        <span>{firstCollection.title}</span>
                      </div>
                    }
                    games={mapCollectionGames(firstCollection.games)}
                    slug={firstCollection.slug}
                  />
                </div>
              )}

              <ProviderTicker />

              <div className={styles.carouselSection}>
                <TableGamesCarousel />
              </div>

              {remainingCollections.map((col) => (
                <div key={col.id} className={styles.carouselSection}>
                  <GameCarousel
                    title={
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                        {getCategoryIcon(col.slug, col.title)}
                        <span>{col.title}</span>
                      </div>
                    }
                    games={mapCollectionGames(col.games)}
                    slug={col.slug}
                  />
                </div>
              ))}
            </>
          )}

          <div className={styles.winnersBoardMobile}>
            <DailySpinWidget />
          </div>

          <BetsTable />
        </div>
      </main>
    </>
  );
}

export default Home;
